// Logo Manager for dynamic logo selection based on background context (PNG format only)
export class LogoManager {
  constructor() {
    this.logoCache = new Map();
  }

  // Clear cache (useful for debugging)
  clearCache() {
    this.logoCache.clear();
  }

  // Get appropriate logo based on background context (PNG format only)
  getLogo(domainConfig, context = 'light') {
    if (!domainConfig) {
      return this.getDefaultLogo(context);
    }

    const cacheKey = `${domainConfig.id}-${context}`;
    
    // Check cache first
    if (this.logoCache.has(cacheKey)) {
      return this.logoCache.get(cacheKey);
    }

    let logoPath;
    
    switch (context) {
      case 'dark':
      case 'hero':
      case 'footer':
        // Use white logo for dark backgrounds
        logoPath = this.getLogoWithPng(domainConfig.whiteLogo) || this.getLogoWithPng(domainConfig.logo);
        break;
      case 'light':
      case 'navbar':
      case 'card':
      default:
        // Use regular logo for light backgrounds
        logoPath = this.getLogoWithPng(domainConfig.logo) || this.getLogoWithPng(domainConfig.whiteLogo);
        break;
    }

    // Fallback to default if no logo found
    if (!logoPath) {
      logoPath = this.getDefaultLogo(context);
    }

    // Cache the result
    this.logoCache.set(cacheKey, logoPath);
    
    return logoPath;
  }

  // Ensure logo path has .png extension
  getLogoWithPng(logoPath) {
    if (!logoPath) return null;

    // If path already has .png extension, return as is
    if (logoPath.match(/\.png$/i)) {
      return logoPath;
    }

    // Remove any existing extension and add .png
    const basePath = logoPath.replace(/\.(jpg|jpeg|png)$/i, '');
    return `${basePath}.png`;
  }

  // Get default logo based on context
  getDefaultLogo(context = 'light') {
    switch (context) {
      case 'dark':
      case 'hero':
      case 'footer':
        return '/assets/logos/default-logo-white.png';
      case 'light':
      case 'navbar':
      case 'card':
      default:
        return '/assets/logos/default-logo.png';
    }
  }

  // Get logo with error handling
  getLogoWithFallback(domainConfig, context = 'light', onError) {
    const logoPath = this.getLogo(domainConfig, context);
    
    return {
      src: logoPath,
      alt: domainConfig?.name || 'Logo',
      onError: onError || this.handleLogoError.bind(this, domainConfig, context)
    };
  }

  // Handle logo loading error
  handleLogoError(domainConfig, context, event) {
    console.warn(`Logo failed to load: ${event.target.src}`);
    
    // Prevent infinite retries by setting a flag
    if (event.target.dataset.retryAttempted) {
      // Already tried fallback, use placeholder
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjUwIiB5PSIyMCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNkI3MjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+TG9nbzwvdGV4dD4KPC9zdmc+';
      return;
    }
    
    // Mark that we've attempted a retry
    event.target.dataset.retryAttempted = 'true';
    
    // Try fallback logo
    const fallbackLogo = this.getDefaultLogo(context);
    if (event.target.src !== fallbackLogo) {
      event.target.src = fallbackLogo;
    } else {
      // If fallback also fails, use a placeholder
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjUwIiB5PSIyMCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNkI3MjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+TG9nbzwvdGV4dD4KPC9zdmc+';
    }
  }


  // Preload logo for better performance
  preloadLogo(domainConfig, context = 'light') {
    const logoPath = this.getLogo(domainConfig, context);
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(logoPath);
      img.onerror = () => reject(new Error(`Failed to preload logo: ${logoPath}`));
      img.src = logoPath;
    });
  }

  // Preload all logo variants for a domain
  async preloadAllLogos(domainConfig) {
    const contexts = ['light', 'dark'];
    const promises = contexts.map(context => 
      this.preloadLogo(domainConfig, context).catch(error => {
        console.warn(`Failed to preload logo for context ${context}:`, error);
        return null;
      })
    );
    
    return Promise.all(promises);
  }
}

// Create singleton instance
export const logoManager = new LogoManager();
